/* =============================================================================
   Monolith — search.js
   Predictive search dropdown under the header pill. Fetches real results from
   Shopify's Predictive Search API (rendered by sections/predictive-search.liquid)
   and supports keyboard navigation. Falls back to the native /search page.
   ============================================================================ */
(function () {
  'use strict';

  const panel = document.querySelector('[data-predictive-search]');
  const backdrop = document.querySelector('[data-predictive-search-backdrop]');
  const results = panel && panel.querySelector('[data-predictive-search-results]');
  if (!panel || !results) return;

  let activeInput = null;
  let abortController = null;
  let lastQuery = '';

  const settings = {
    limit: parseInt(panel.dataset.limit || '6', 10)
  };

  function open() {
    panel.hidden = false;
    if (backdrop) backdrop.hidden = false;
    requestAnimationFrame(() => {
      panel.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-visible');
    });
    if (activeInput) activeInput.setAttribute('aria-expanded', 'true');
  }

  function close() {
    panel.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-visible');
    setTimeout(() => {
      if (!panel.classList.contains('is-open')) {
        panel.hidden = true;
        if (backdrop) backdrop.hidden = true;
      }
    }, 220);
    if (activeInput) activeInput.setAttribute('aria-expanded', 'false');
  }

  function clear() {
    results.innerHTML = '';
    lastQuery = '';
    close();
  }

  async function search(query) {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      clear();
      return;
    }
    if (trimmed === lastQuery) {
      open();
      return;
    }
    lastQuery = trimmed;

    if (abortController) abortController.abort();
    abortController = new AbortController();

    const resources = ['product'];
    if (panel.dataset.showCollections !== 'false') resources.push('collection');
    if (panel.dataset.showPages !== 'false') resources.push('page', 'article');

    const url =
      `${theme.routes.predictiveSearch}?q=${encodeURIComponent(trimmed)}` +
      `&resources[type]=${resources.join(',')}` +
      `&resources[limit]=${settings.limit}` +
      `&resources[options][unavailable_products]=last` +
      `&resources[options][fields]=title,product_type,variants.title,vendor,tag` +
      `&section_id=predictive-search`;

    results.classList.add('is-loading');
    try {
      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok) throw new Error(response.status);
      const html = await response.text();
      const inner = theme.getSectionInnerHTML(html, '[data-predictive-search-content]');
      results.innerHTML = inner;
      open();
    } catch (error) {
      if (error.name !== 'AbortError') {
        results.innerHTML = `<p class="predictive-search__empty">${theme.strings.error}</p>`;
        open();
      }
    } finally {
      results.classList.remove('is-loading');
    }
  }

  const debouncedSearch = theme.debounce(search, 250);

  function bindInput(input) {
    if (input.dataset.predictiveBound) return;
    input.dataset.predictiveBound = 'true';
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', 'PredictiveSearch');
    input.setAttribute('aria-autocomplete', 'list');

    input.addEventListener('input', () => {
      activeInput = input;
      debouncedSearch(input.value);
    });
    input.addEventListener('focus', () => {
      activeInput = input;
      if (input.value.trim().length >= 2 && results.children.length) open();
    });
    input.addEventListener('keydown', (event) => {
      const items = Array.from(results.querySelectorAll('[data-predictive-search-item]'));
      if (!items.length) return;
      const current = items.findIndex((el) => el.classList.contains('is-focused'));
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const next = event.key === 'ArrowDown' ? Math.min(items.length - 1, current + 1) : Math.max(0, current - 1);
        items.forEach((el, i) => el.classList.toggle('is-focused', i === next));
        items[next].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && current >= 0) {
        event.preventDefault();
        window.location.href = items[current].href;
      }
    });
  }

  function init(root = document) {
    root.querySelectorAll('[data-predictive-search-input]').forEach(bindInput);
  }

  if (backdrop) backdrop.addEventListener('click', () => {
    close();
    theme.events.emit('search:request-close');
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });
  document.addEventListener('click', (event) => {
    if (!panel.contains(event.target) && !event.target.closest('[data-predictive-search-input]') && !event.target.closest('[data-search-toggle]')) close();
  });

  theme.events.on('search:close', clear);
  theme.events.on('search:open', ({ input }) => {
    activeInput = input;
  });
  theme.events.on(theme.EVENTS.drawerOpen, close);

  theme.onDocumentReady(() => init());
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
